import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../redux/store";
import {
  ElectricityPurchaseRequest,
  ElectricityPurchaseState,
  PurchaseSuccessPayload,
  SourceType,
  Transaction,
  PurchaseResponse,
} from "./types/electricity-types";

// =============================
// Types inferred from backend
// =============================

// =============================
// API Base URL / Endpoint
// =============================
const API_URL = process.env.REACT_APP_API_URL;
const PURCHASE_ENDPOINT = `${API_URL}/electric-purchase`;

// =============================
// Thunk: purchase electricity
// =============================
export const purchaseElectricity = createAsyncThunk<
  PurchaseResponse,
  ElectricityPurchaseRequest,
  { rejectValue: { message: string }; state: RootState }
>("electricityPurchase/purchaseElectricity", async (payload, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  if (!token) {
    console.error("❌ No token found in Redux state");
    return thunkAPI.rejectWithValue({ message: "Unauthorized" });
  }
  try {
    // Normalize/uppercase source for backend expectations
    const normalized = {
      ...payload,
      source: payload.source?.toUpperCase() as SourceType | undefined,
    };
    console.log("🔍 Before axios request - normalized payload:", normalized);
    console.log("Amount type:", typeof normalized.amount);
    console.log("Amount value:", normalized.amount);

    const res = await axios.post(PURCHASE_ENDPOINT, normalized, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    // Backend returns shapes like:
    // { status: 'success', message: 'ATM Purchase initiated', data: { payment_url } }
    // { status: 'success', message: 'Purchase successful', data: { ...transaction } }
    // { status: 'success', message: 'Transaction is processing', data: { ...transaction } }
    // { status: 'error', message: 'Insufficient Coins for transaction!' }
    return res.data as PurchaseResponse;
  } catch (err: any) {
    const message =
      err?.response?.data?.message || err?.message || "Purchase failed";
    return thunkAPI.rejectWithValue({ message });
  }
});

// =============================
// Slice
// =============================

const initialState: ElectricityPurchaseState = {
  loading: false,
  error: null,
  message: null,
  transaction: null,
  paymentUrl: null,
  lastStatus: "idle",
};

const electricityPurchaseSlice = createSlice({
  name: "electricityPurchase",
  initialState,
  reducers: {
    resetPurchaseState: () => initialState,
    setPaymentUrl: (state, action: PayloadAction<string | null>) => {
      state.paymentUrl = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(purchaseElectricity.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
        state.paymentUrl = null;
        state.transaction = null;
        state.lastStatus = "initiated";
      })
      .addCase(purchaseElectricity.fulfilled, (state, action) => {
        state.loading = false;
        state.message = action.payload.message;

        if (action.payload.status === "error") {
          state.error = action.payload.message || "Purchase error";
          state.lastStatus = "error";
          return;
        }

        // success path
        state.error = null;
        const data = (action.payload as PurchaseSuccessPayload).data;

        // ATM flow: data.payment_url present
        if (
          data &&
          typeof data === "object" &&
          "payment_url" in data &&
          (data as any).payment_url
        ) {
          state.paymentUrl = (data as any).payment_url as string;
          state.lastStatus = "initiated"; // awaiting user to complete ATM payment
          return;
        }

        // Otherwise, backend may return the transaction object
        if (data && typeof data === "object") {
          state.transaction = data as Transaction;

          const s = (data as any).status as string | undefined;
          if (s === "SUCCESSFUL") {
            state.lastStatus = "successful";
          } else if (
            s === "AWAITINGPAYMENT" ||
            s === "PROCESSING" ||
            s === "QUEUED"
          ) {
            state.lastStatus = "processing";
          } else if (s === "FAILED" || s === "CANCELLED") {
            state.lastStatus = "error";
          } else {
            state.lastStatus = "processing";
          }
        }
      })
      .addCase(purchaseElectricity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Purchase failed";
        state.lastStatus = "error";
      });
  },
});

export const { resetPurchaseState, setPaymentUrl } =
  electricityPurchaseSlice.actions;
export default electricityPurchaseSlice.reducer;

// =============================
// Selectors
// =============================
export const selectPurchaseLoading = (state: RootState) =>
  state.purchaseElectricity.loading;
export const selectPurchaseError = (state: RootState) =>
  state.purchaseElectricity.error;
export const selectPurchaseMessage = (state: RootState) =>
  state.purchaseElectricity.message;
export const selectPurchasePaymentUrl = (state: RootState) =>
  state.purchaseElectricity.paymentUrl;
export const selectPurchaseTransaction = (state: RootState) =>
  state.purchaseElectricity.transaction;
export const selectPurchaseStatus = (state: RootState) =>
  state.purchaseElectricity.lastStatus;
